import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trash2, UserPlus, Search, Link2, Copy, Check, RotateCw, GitCompare, X } from "lucide-react";
import { toast } from "sonner";
import SeatUsagePill from "@/components/SeatUsagePill";
import DepartmentPills from "@/components/DepartmentPills";
import TierLimitBanner from "@/components/TierLimitBanner";
import TierUpgradeDialog from "@/components/TierUpgradeDialog";

export default function CoachRoster() {
  const [roster, setRoster] = useState([]);
  const [invites, setInvites] = useState([]);
  const [verdictsById, setVerdictsById] = useState({});
  const [riskById, setRiskById] = useState({});
  const [riskSort, setRiskSort] = useState(false);
  const [q, setQ] = useState("");
  const [posFilter, setPosFilter] = useState("all"); // 'all' | 'pitcher' | 'position' | 'twoway'
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("password"); // 'password' | 'invite'
  const [form, setForm] = useState({ name: "", email: "", password: "armiq123", year: "", dominant_arm: "R", position: "PO", bats: "R", throws: "R" });
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState("");
  const [teamInviteToken, setTeamInviteToken] = useState("");
  const [rotating, setRotating] = useState(false);
  const [tierInfo, setTierInfo] = useState(null);     // { tier, meta, limits }
  const [allTiers, setAllTiers] = useState(null);     // pricing-catalog tier names
  const [selected, setSelected] = useState({});       // { user_id: true } for compare
  const [compareOpen, setCompareOpen] = useState(false);

  const inviteUrl = (token) => `${window.location.origin}/invite/${token}`;
  const teamInviteFullUrl = teamInviteToken ? `${window.location.origin}/team-invite/${teamInviteToken}` : "";

  const loadTeamInvite = async () => {
    try { const { data } = await api.get("/teams/me/invite-link"); setTeamInviteToken(data.token); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const regenTeamInvite = async () => {
    if (!window.confirm("Rotate the team invite link? The old URL will stop working immediately.")) return;
    setRotating(true);
    try {
      const { data } = await api.post("/teams/me/invite-link/regenerate");
      setTeamInviteToken(data.token);
      toast.success("New invite link generated");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRotating(false); }
  };
  const copyTeamInvite = async () => {
    if (!teamInviteFullUrl) return;
    try { await navigator.clipboard.writeText(teamInviteFullUrl); toast.success("Team invite link copied"); }
    catch { toast.error("Couldn't copy — long-press to copy"); }
  };

  const load = async () => {
    const [r, i] = await Promise.all([api.get("/players"), api.get("/player-invites")]);
    setRoster(r.data);
    setInvites(i.data.filter((x) => x.status !== "accepted"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => {
    // Pull tier + live limits — drives the in-app upgrade banner.
    (async () => {
      try {
        const [tierRes, pkgRes] = await Promise.all([
          api.get("/billing/tier"),
          api.get("/billing/packages"),
        ]);
        setTierInfo(tierRes.data);
        setAllTiers(pkgRes.data.tiers);
      } catch { /* silent — banner just hides */ }
    })();
  }, []);
  useEffect(() => {
    // Lazy-fetch the Department verdict roll-up so the table can render Pills inline.
    // Falls back gracefully when the endpoint isn't available.
    (async () => {
      try {
        const { data } = await api.get("/department/roster");
        const map = {};
        (data.items || []).forEach((v) => { map[v.player_user_id] = v; });
        setVerdictsById(map);
      } catch { /* silent — pills will render '—' */ }
    })();
  }, []);
  useEffect(() => {
    // Predictive injury-risk roster — worst-first triage view.
    (async () => {
      try {
        // Resolve team_id from any roster row; API is /team/{team_id}/roster.
        const meRes = await api.get("/auth/me");
        const teamId = meRes.data?.team_id || meRes.data?.user?.team_id;
        if (!teamId) return;
        const { data } = await api.get(`/injury-risk/team/${teamId}/roster`);
        const map = {};
        (data.rows || []).forEach((r) => { map[r.player_user_id] = r; });
        setRiskById(map);
      } catch { /* silent — column just renders '—' */ }
    })();
  }, []);
  useEffect(() => { if (open && mode === "invite" && !teamInviteToken) loadTeamInvite();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open, mode]);

  // Position-bucket filter — matches both structured codes (PO / 2-Way / C / 1B…)
  // AND legacy free-text labels like "Starting Pitcher" / "Catcher/Pitcher".
  const positionBucket = (pos) => {
    const p = (pos || "").toUpperCase().trim();
    if (!p) return "unknown";
    if (p === "PO") return "pitcher";
    if (p === "2-WAY" || p === "TWO-WAY" || p === "TWOWAY") return "twoway";
    // Legacy data: "Catcher/Pitcher" or "P/SS" → 2-way style
    if (p.includes("/") || p.includes("&")) return "twoway";
    if (/PITCH|RHP|LHP|\bP\b/.test(p)) return "pitcher";
    return "position";
  };
  const bucketCounts = roster.reduce(
    (acc, p) => {
      const b = positionBucket(p.profile?.position);
      acc[b] = (acc[b] || 0) + 1;
      acc.all += 1;
      return acc;
    },
    { all: 0, pitcher: 0, position: 0, twoway: 0 },
  );
  const filtered = roster.filter((p) => {
    const matchesText = `${p.name} ${p.email} ${p.profile?.position || ""}`.toLowerCase().includes(q.toLowerCase());
    if (!matchesText) return false;
    if (posFilter === "all") return true;
    return positionBucket(p.profile?.position) === posFilter;
  });

  const displayed = riskSort
    ? [...filtered].sort((a, b) => {
        const rank = { critical: 0, high: 1, moderate: 2, low: 3 };
        const ra = riskById[a.user_id]; const rb = riskById[b.user_id];
        const bandA = rank[ra?.band] ?? 99;
        const bandB = rank[rb?.band] ?? 99;
        if (bandA !== bandB) return bandA - bandB;
        return (rb?.score ?? -1) - (ra?.score ?? -1);
      })
    : filtered;

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const displayedSelectedCount = displayed.filter((p) => selected[p.user_id]).length;
  const allDisplayedSelected = displayed.length > 0 && displayedSelectedCount === displayed.length;
  const toggleAllDisplayed = () => {
    if (allDisplayedSelected) {
      const next = { ...selected };
      displayed.forEach((p) => delete next[p.user_id]);
      setSelected(next);
    } else {
      const next = { ...selected };
      displayed.forEach((p) => { next[p.user_id] = true; });
      setSelected(next);
    }
  };
  const toggleRow = (uid) => setSelected((s) => ({ ...s, [uid]: !s[uid] }));

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const [upgradeNotice, setUpgradeNotice] = useState(null);

  const submitCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      if (mode === "password") {
        await api.post("/players", form);
        toast.success(`${form.name} added — initial password: ${form.password}`);
      } else {
        const { data } = await api.post("/player-invites", {
          name: form.name, email: form.email || null, year: form.year,
          dominant_arm: form.dominant_arm, position: form.position,
          bats: form.bats, throws: form.throws,
        });
        const url = inviteUrl(data.token);
        await navigator.clipboard.writeText(url).catch(() => {});
        toast.success(`Invite link copied for ${form.name}`);
      }
      setOpen(false);
      setForm({ name: "", email: "", password: "armiq123", year: "", dominant_arm: "R", position: "PO", bats: "R", throws: "R" });
      load();
    } catch (err) {
      // Tier limit hit — surface a structured upgrade prompt instead of a toast.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402 && detail?.error === "tier_limit_reached") {
        setOpen(false);
        setUpgradeNotice(detail);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name} from the roster?`)) return;
    try { await api.delete(`/players/${id}`); load(); toast.success(`${name} removed`); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const copyInvite = async (token) => {
    const url = inviteUrl(token);
    await navigator.clipboard.writeText(url).catch(() => {});
    setCopied(token);
    setTimeout(() => setCopied(""), 1600);
    toast.success("Invite link copied");
  };

  const cancelInvite = async (token, name) => {
    if (!window.confirm(`Cancel invite for ${name}?`)) return;
    try { await api.delete(`/player-invites/${token}`); load(); toast.success("Invite cancelled"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="coach-roster-page">
      <SeatUsagePill />
      {tierInfo?.limits?.athletes && (
        <TierLimitBanner limit={tierInfo.limits.athletes} label="athletes" tierMeta={allTiers} />
      )}
      <TierUpgradeDialog notice={upgradeNotice} onClose={() => setUpgradeNotice(null)} />
      <CompareDialog
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        athletes={roster.filter((p) => selected[p.user_id])}
        verdictsById={verdictsById}
        riskById={riskById}
      />
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">Roster</div>
          <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter mt-1 text-white">Athletes</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-player-button" className="text-white red-glow font-semibold" style={{ backgroundColor: "var(--brand-secondary)" }}>
              <UserPlus className="h-4 w-4 mr-2" /> Add player
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[var(--ink-2)] border-[var(--ink-border)] text-white">
            <DialogHeader>
              <DialogTitle className="text-white">Add a player</DialogTitle>
              <DialogDescription className="text-white/55 text-sm">
                Either issue an invite link or create the account with a starter password.
              </DialogDescription>
            </DialogHeader>
            <Tabs value={mode} onValueChange={setMode}>
              <TabsList className="grid grid-cols-2 bg-[var(--ink-1)]">
                <TabsTrigger value="password" data-testid="tab-direct-add">Direct add</TabsTrigger>
                <TabsTrigger value="invite" data-testid="tab-invite-link">Invite link</TabsTrigger>
              </TabsList>

              {/* INVITE LINK MODE — zero inputs, just generate-copy-share */}
              <TabsContent value="invite" className="mt-4" data-testid="invite-link-pane">
                <div className="text-sm text-white/70">
                  Share this link with as many recruits as you want. Anyone who opens it creates their own account inside your team.
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <Input
                    readOnly
                    value={teamInviteFullUrl || "Generating link…"}
                    className="bg-[var(--ink-1)] border-[var(--ink-border)] text-white font-mono-num text-sm flex-1"
                    onFocus={(e) => e.target.select()}
                    data-testid="team-invite-url"
                  />
                  <Button onClick={copyTeamInvite} disabled={!teamInviteFullUrl} className="text-white font-bold red-glow" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="team-invite-copy">
                    <Copy className="h-4 w-4 mr-2" /> Copy
                  </Button>
                  <Button variant="outline" disabled={rotating || !teamInviteFullUrl} onClick={regenTeamInvite} className="border-white/15 text-white bg-white/5 hover:bg-white/10" title="Rotate this link — old URL stops working" data-testid="team-invite-regenerate">
                    <RotateCw className={`h-4 w-4 ${rotating ? "animate-spin" : ""}`} />
                  </Button>
                </div>
                <div className="mt-3 text-xs text-white/40">
                  Tip: post this link in your team's group chat or print it as a QR code in the locker room.
                </div>
              </TabsContent>

              {/* DIRECT ADD MODE — keep the existing prefilled form */}
              <TabsContent value="password" className="mt-2">
                <form onSubmit={submitCreate} className="space-y-4" data-testid="add-player-form">
                  <div>
                    <Label className="text-white/70">Name</Label>
                    <Input required value={form.name} onChange={upd("name")} placeholder="Full name" className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid="player-name-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-white/70">Email</Label>
                      <Input required type="email" value={form.email} onChange={upd("email")} placeholder="player@team.com" className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid="player-email-input" />
                    </div>
                    <div>
                      <Label className="text-white/70">Initial password</Label>
                      <Input required value={form.password} onChange={upd("password")} className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid="player-password-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-white/70">Year</Label>
                      <Input value={form.year} onChange={upd("year")} placeholder="Senior" className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid="player-year-input" />
                    </div>
                    <div>
                      <Label className="text-white/70">Position</Label>
                      <select value={form.position} onChange={upd("position")} className="mt-1.5 h-10 w-full border border-[var(--ink-border)] rounded-md px-3 text-sm bg-[var(--ink-1)] text-white" data-testid="player-position-input">
                        <option value="PO">PO · Pitcher Only</option>
                        <option value="2-Way">2-Way</option>
                        <option value="C">C · Catcher</option>
                        <option value="1B">1B</option>
                        <option value="2B">2B</option>
                        <option value="3B">3B</option>
                        <option value="SS">SS</option>
                        <option value="OF">OF · Outfield</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-white/70">Dominant Arm</Label>
                      <select value={form.dominant_arm} onChange={upd("dominant_arm")} className="mt-1.5 h-10 w-full border border-[var(--ink-border)] rounded-md px-3 text-sm bg-[var(--ink-1)] text-white" data-testid="player-arm-input">
                        <option value="R">Right</option>
                        <option value="L">Left</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-white/70">Bats</Label>
                      <select value={form.bats} onChange={upd("bats")} className="mt-1.5 h-10 w-full border border-[var(--ink-border)] rounded-md px-3 text-sm bg-[var(--ink-1)] text-white" data-testid="player-bats-input">
                        <option value="R">Right</option>
                        <option value="L">Left</option>
                        <option value="Both">Switch · Both</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-white/70">Throws</Label>
                      <select value={form.throws} onChange={upd("throws")} className="mt-1.5 h-10 w-full border border-[var(--ink-border)] rounded-md px-3 text-sm bg-[var(--ink-1)] text-white" data-testid="player-throws-input">
                        <option value="R">Right</option>
                        <option value="L">Left</option>
                        <option value="Both">Both</option>
                      </select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={creating} className="text-white font-bold red-glow" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="player-create-submit">
                      {creating ? "Working…" : "Create player"}
                    </Button>
                  </DialogFooter>
                </form>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, position…" className="pl-9 bg-[var(--ink-2)] border-[var(--ink-border)] text-white" data-testid="roster-search-input" />
        </div>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="roster-position-filter">
          {[
            { v: "all",      label: "All",                count: bucketCounts.all },
            { v: "pitcher",  label: "Pitchers · PO",      count: bucketCounts.pitcher },
            { v: "twoway",   label: "Two-Way",            count: bucketCounts.twoway },
            { v: "position", label: "Position Players",   count: bucketCounts.position },
          ].map((opt) => {
            const active = posFilter === opt.v;
            return (
              <button
                key={opt.v}
                onClick={() => setPosFilter(opt.v)}
                data-testid={`roster-position-chip-${opt.v}`}
                className={`px-3 h-9 rounded-full text-xs font-bold transition border ${active ? "text-white border-transparent" : "text-white/70 border-[var(--ink-border)] bg-[var(--ink-2)] hover:text-white hover:border-white/30"}`}
                style={active ? { backgroundColor: "var(--brand-secondary)" } : {}}
              >
                {opt.label}
                <span className={`ml-1.5 font-mono-num ${active ? "text-white/80" : "text-white/40"}`}>{opt.count}</span>
              </button>
            );
          })}
        </div>
        {selectedCount > 0 && (
          <div className="ml-auto flex items-center gap-2" data-testid="roster-compare-toolbar">
            <span className="text-xs text-white/60 font-mono-num" data-testid="roster-selected-count">
              {selectedCount} selected
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelected({})}
              className="border border-white/15 text-white/70 hover:bg-white/10 text-xs"
              data-testid="roster-clear-selection"
            >
              <X className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
            <Button
              size="sm"
              disabled={selectedCount < 2}
              onClick={() => setCompareOpen(true)}
              className="text-white font-semibold text-xs"
              style={{ backgroundColor: "var(--brand-secondary)" }}
              data-testid="roster-compare-button"
            >
              <GitCompare className="h-3.5 w-3.5 mr-1.5" /> Compare selected
            </Button>
          </div>
        )}
      </div>

      {invites.length > 0 && (
        <div className="rounded-xl ink-card p-5">
          <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Pending invites · {invites.length}</div>
          <ul className="mt-3 divide-y divide-[var(--ink-border)]">
            {invites.map((inv) => (
              <li key={inv.token} className="flex items-center gap-3 py-3" data-testid={`invite-row-${inv.token}`}>
                <Link2 className="h-4 w-4 text-white/40" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-white">{inv.name}</div>
                  <div className="text-xs text-white/40 truncate font-mono">{inviteUrl(inv.token)}</div>
                </div>
                <Button variant="outline" size="sm" onClick={() => copyInvite(inv.token)} className="border-white/15 text-white/80 bg-white/5 hover:bg-white/10" data-testid={`copy-invite-${inv.token}`}>
                  {copied === inv.token ? <><Check className="h-3.5 w-3.5 mr-1.5" /> Copied</> : <><Copy className="h-3.5 w-3.5 mr-1.5" /> Copy link</>}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => cancelInvite(inv.token, inv.name)} data-testid={`cancel-invite-${inv.token}`}>
                  <Trash2 className="h-4 w-4 text-white/40 hover:text-red-400" />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-xl ink-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-[var(--ink-border)] text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">
            <tr>
              <th className="text-center px-3 py-3 w-10">
                <input
                  type="checkbox"
                  aria-label="Select all displayed athletes"
                  checked={allDisplayedSelected}
                  onChange={toggleAllDisplayed}
                  className="accent-fuchsia-500 h-4 w-4 cursor-pointer"
                  data-testid="roster-select-all"
                />
              </th>
              <th className="text-left px-4 py-3">Athlete</th>
              <th className="text-left px-4 py-3">Position</th>
              <th className="text-left px-4 py-3">Year</th>
              <th className="text-right px-4 py-3">7-Day Vol</th>
              <th className="text-left px-4 py-3">Department</th>
              <th className="text-center px-4 py-3">
                <button
                  type="button"
                  onClick={() => setRiskSort((v) => !v)}
                  className={`inline-flex items-center gap-1 uppercase tracking-[0.25em] ${riskSort ? "text-red-400" : "text-white/60 hover:text-white"}`}
                  data-testid="roster-risk-sort-toggle"
                  title="Sort worst-first"
                >
                  Risk {riskSort ? "▼" : "↕"}
                </button>
              </th>
              <th className="text-right px-4 py-3">Readiness</th>
              <th className="text-center px-4 py-3">Suggested</th>
              <th className="text-right px-4 py-3">Status</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--ink-border)]">
            {displayed.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-10 text-center text-white/40 text-sm">No matching players.</td></tr>
            )}
            {displayed.map((p) => {
              const score = p.readiness?.score;
              const suggested = p.readiness?.suggested_hybrid;
              const hybridColor = { A: "#D90429", B: "#F59E0B", C: "#22D3EE", D: "#16A34A" }[suggested] || "#64748B";
              const riskRow = riskById[p.user_id];
              const riskColor = {
                critical: "#EF4444", high: "#F59E0B", moderate: "#22D3EE", low: "#10B981",
              }[riskRow?.band] || "#64748B";
              const isSelected = !!selected[p.user_id];
              return (
              <tr key={p.user_id}
                  className={`hover:bg-white/[0.03] ${isSelected ? "bg-fuchsia-500/[0.06]" : ""}`}
                  data-testid={`roster-row-${p.user_id}`}>
                <td className="px-3 py-3 text-center">
                  <input
                    type="checkbox"
                    aria-label={`Select ${p.name} for compare`}
                    checked={isSelected}
                    onChange={() => toggleRow(p.user_id)}
                    className="accent-fuchsia-500 h-4 w-4 cursor-pointer"
                    data-testid={`roster-select-${p.user_id}`}
                  />
                </td>
                <td className="px-4 py-3">
                  <Link to={`/dashboard/player/${p.user_id}`} className="flex items-center gap-3">
                    {p.avatar_url ? (
                      <img src={p.avatar_url} alt={p.name} referrerPolicy="no-referrer" className="w-9 h-9 rounded-full object-cover ring-1 ring-white/10" data-testid={`roster-avatar-${p.user_id}`} />
                    ) : (
                      <div className="w-9 h-9 rounded-full flex items-center justify-center font-display font-black text-white text-xs ring-1 ring-white/10" style={{ backgroundColor: "var(--brand-primary)" }}>
                        {p.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                      </div>
                    )}
                    <div>
                      <div className="font-semibold text-white">{p.name}</div>
                      <div className="text-xs text-white/40">{p.email}</div>
                    </div>
                  </Link>
                </td>
                <td className="px-4 py-3 text-white/70">{p.profile?.position || "—"}</td>
                <td className="px-4 py-3 text-white/70">{p.profile?.year || "—"}</td>
                <td className="px-4 py-3 text-right font-mono-num font-semibold text-white">{p.workload_7d}</td>
                <td className="px-4 py-3" data-testid={`roster-dept-${p.user_id}`}>
                  <DepartmentPills verdict={verdictsById[p.user_id]} compact testId={`roster-dept-pills-${p.user_id}`} />
                </td>
                <td className="px-4 py-3 text-center" data-testid={`roster-risk-${p.user_id}`}>
                  {riskRow ? (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] border font-mono-num"
                      style={{ color: riskColor, borderColor: `${riskColor}80`, backgroundColor: `${riskColor}18` }}
                      title={`Predictive injury risk · ${riskRow.band} (${riskRow.factors?.length || 0} factor${riskRow.factors?.length === 1 ? "" : "s"})`}
                    >
                      <span>{riskRow.score}</span>
                      <span className="opacity-60">·</span>
                      <span>{riskRow.band}</span>
                    </span>
                  ) : (
                    <span className="text-white/30 text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right font-mono-num font-semibold text-white" data-testid={`roster-readiness-${p.user_id}`}>
                  {score != null ? score : "—"}
                </td>
                <td className="px-4 py-3 text-center">
                  {suggested ? (
                    <span
                      className="inline-flex items-center justify-center w-9 h-9 rounded-full font-display font-black text-base ring-2"
                      style={{ color: hybridColor, borderColor: hybridColor, backgroundColor: `${hybridColor}1a` }}
                      title={`Suggested Hybrid ${suggested} based on readiness ${score}`}
                      data-testid={`roster-suggested-${p.user_id}`}
                    >
                      {suggested}
                    </span>
                  ) : <span className="text-white/30 text-xs">—</span>}
                </td>
                <td className="px-4 py-3 text-right"><StatusBadge status={p.status} /></td>
                <td className="px-4 py-3 text-right">
                  <Button variant="ghost" size="icon" onClick={() => remove(p.user_id, p.name)} data-testid={`delete-player-${p.user_id}`}>
                    <Trash2 className="h-4 w-4 text-white/40 hover:text-red-400" />
                  </Button>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── CompareDialog ────────────────────────────────────────────────────
// Side-by-side stat card for a coach's selected athletes. Reuses data
// the roster already fetched (verdicts, injury-risk) so there's no
// extra API call — just a focused, print-friendly comparison view.
function CompareDialog({ open, onClose, athletes, verdictsById, riskById }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4 py-6"
      role="dialog"
      aria-modal="true"
      data-testid="roster-compare-dialog"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-6xl max-h-[90vh] overflow-auto rounded-2xl bg-[var(--ink-2)] border border-[var(--ink-border)] text-white shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--ink-border)] sticky top-0 bg-[var(--ink-2)] z-10">
          <div>
            <div className="text-[10px] tracking-[0.3em] font-bold text-fuchsia-300/80 uppercase">Compare</div>
            <div className="font-display font-black text-2xl">
              {athletes.length} athletes side-by-side
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="border border-white/15 text-white/70 hover:bg-white/10"
            data-testid="roster-compare-close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-6 grid gap-4"
             style={{ gridTemplateColumns: `repeat(${Math.min(athletes.length, 4)}, minmax(0, 1fr))` }}
             data-testid="roster-compare-grid">
          {athletes.map((p) => {
            const risk = riskById[p.user_id];
            const dept = verdictsById[p.user_id];
            const riskColor = { critical: "#EF4444", high: "#F59E0B", moderate: "#22D3EE", low: "#10B981" }[risk?.band] || "#64748B";
            return (
              <div key={p.user_id} className="rounded-xl border border-[var(--ink-border)] bg-[var(--ink-1)] p-5"
                   data-testid={`roster-compare-card-${p.user_id}`}>
                <div className="flex items-center gap-3 mb-4">
                  {p.avatar_url ? (
                    <img src={p.avatar_url} alt={p.name} referrerPolicy="no-referrer" className="w-11 h-11 rounded-full object-cover ring-1 ring-white/10" />
                  ) : (
                    <div className="w-11 h-11 rounded-full flex items-center justify-center font-display font-black text-white text-sm ring-1 ring-white/10" style={{ backgroundColor: "var(--brand-primary)" }}>
                      {p.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-display font-black text-white truncate">{p.name}</div>
                    <div className="text-[11px] text-white/50 truncate">{p.profile?.position || "—"} · {p.profile?.year || "—"}</div>
                  </div>
                </div>

                <StatRow k="Readiness"       v={p.readiness?.score != null ? p.readiness.score : "—"} />
                <StatRow k="Suggested"       v={p.readiness?.suggested_hybrid || "—"} />
                <StatRow k="7-day workload"  v={p.workload_7d ?? "—"} />
                <StatRow
                  k="Injury risk"
                  v={risk ? `${risk.score} · ${risk.band}` : "—"}
                  color={risk ? riskColor : undefined}
                />
                <StatRow k="Status"          v={p.status || "—"} />
                <StatRow k="Department flags" v={dept?.total_flags != null ? dept.total_flags : (dept?.verdict || "—")} />

                {risk?.factors?.length ? (
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/45 mb-1">Top risk drivers</div>
                    <ul className="text-xs text-white/75 space-y-0.5 list-disc pl-4">
                      {risk.factors.slice(0, 3).map((f, i) => (
                        <li key={i}>{typeof f === "string" ? f : (f?.label || JSON.stringify(f))}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <div className="mt-4">
                  <Link to={`/dashboard/player/${p.user_id}`}
                        className="text-xs text-fuchsia-300 hover:text-fuchsia-200 underline"
                        onClick={onClose}
                        data-testid={`roster-compare-open-${p.user_id}`}>
                    Open athlete profile →
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatRow({ k, v, color }) {
  return (
    <div className="flex items-center justify-between text-sm py-1.5 border-b border-[var(--ink-border)] last:border-b-0">
      <span className="text-white/50">{k}</span>
      <span className="font-mono-num font-semibold tabular-nums" style={color ? { color } : undefined}>{v}</span>
    </div>
  );
}
